import { BootcampPage, CatalogPageCustomization } from "@/types/Customizations";
import { PartialPartner, Partner } from "@/common/partner/types";
import { CourseRun } from "@/common/courseRun/types";
import { PriceRange, ProgramType } from "@/common/program/types";
import { Instructor } from "@/common/instructor/types";

export enum ProductType {
  EXECUTIVE_EDUCATION = "Executive Education",
  BOOTCAMP = "Boot Camp",
  COURSE = "Course",
}

export type PartialProgram = {
  subtitle: string;
  title: string;
  uuid: string;
  type?: ProgramType;
  marketingUrl?: string;
  priceRanges?: PriceRange[];
  externalId?: string;
};

export type EnrichedProgram = PartialProgram & {
  pacing?: string;
  cardImageUrl?: string;
  weeksToComplete?: number;
  weeksToCompleteMin?: number;
  weeksToCompleteMax?: number;
  minHoursEffortPerWeek?: number;
  maxHoursEffortPerWeek?: number;
  courses?: {
    title?: string | null;
    uuid?: string | null;
  }[];
  partners?: {
    name?: string;
    logoUrl?: string;
  }[];
  pricingData?: {
    originalPrice: number;
    discountedPrice: number;
    currency: string;
  };
  subjects?: string[];
};

export type Video = {
  src: string | null;
  description: string | null;
  image: {
    description: string | null;
    src: string | null;
    width: number | null;
    height: number | null;
  };
};

export type Redirect = {
  created: Date;
  modified: Date;
  partnerId: number;
  value: string;
};

type BaseCourseSubject = {
  languageCode: string;
  name: string;
  slug: string;
  sortValue?: number | null;
  uuid: string;
};

export type CourseSubject = BaseCourseSubject & {
  description?: string;
  masterId: number;
  subtitle: string;
};

export type ProcessedCourseSubject = BaseCourseSubject & {
  inEdx?: boolean;
};

export type Collaborator = {
  created: string;
  image: string;
  modified: string;
  name: string;
  uuid: string;
};

export type Sponsor = {
  key: string;
  logoImageUrl: string;
  name: string;
  slug: string;
  uuid: string;
};

type BaseCourse = {
  id: string;
  key: string;
  isDeleted: boolean;
  created: Date;
  externalId: string;
  canonicalCourseRunKey: string | null;
  productUuid: string;
  productName: string;
  productShortDescription: string;
  productType: ProductType;
  productFullDescription: string;
  productOverview: string;
  productUrl: string;
  productUrlSlug: string;
  productImageUrl: string;
  productVariantUuid?: string;
  objectID: string;
  // partnerKey: string;
  /** @deprecated Please use Course.Partners */
  partner?: Partner | null;
  partners: PartialPartner[];
  aboutThisCourse?: string;
  subjectEs: string;
  subjectEn: string;
  level?: string;
  prerequisites: string[];
  productVariantLanguageId?: string;
  partnerNameOverride: string | null;
  partnerLogoOverride: string | null;
  associatedSkills: string[];
  /** @deprecated Please use Course.productOverview */
  whatYoullLearn?: string[];
  frequentlyAskedQuestions: string | null;
  /** @deprecated Nothing ever sets this value. Please use subjects */
  attributes?: string[];
  courseRunStatuses: { key: string; status: string; uuid: string }[];
  courseRunKeys: string[];
  courseRuns?: CourseRun[];
  hash?: string;
  skills: {
    description: string;
    infoUrl: string;
    typeId: string;
    typeName: string;
    skill: string;
    category: string;
    subcategory: string;
  }[];
  tags?: string[];
  redirects: Redirect[];
  urlSlugHistory: {
    slug: string;
    isActive: boolean;
  }[];
  programs: PartialProgram[] | null;
  customizations: {
    catalogPage: CatalogPageCustomization | null;
    bootcampPage: BootcampPage | null;
  };
  prerequisitesHtml: string;
  ratings: {
    average: number;
    count: number;
  } | null;
  syllabusHtml: string;
  modified: Date;
  advertisedCourseRunUuid: string;
  hasOfacRestrictions: boolean | null;
  enrollmentCount: number;
  recentEnrollmentCount?: number | null;
  learnerTestimonials: string | null;
  video: Video;
  levelType: string;
  productSource: string | null;
  sponsors: Sponsor[];
  collaborators: Collaborator[];
  additionalInformation: string;
  sortValue?: number | null;
  showInAlgoliaSearchResults: boolean;
  availability: string[];
  /** Optional price ranges from the Partner API; not currently populated by getPublishedCourses */
  priceRanges?: PriceRange[];
  /** Unique instructors for this course (de-duplicated from all course runs) */
  instructors?: Omit<Instructor, "aboutMe">[];
};

export type Course = BaseCourse & {
  additionalMetadata: AdditionalMetadata[];
  subjects: CourseSubject[] | null;
};

export type CourseEntitlement = {
  mode: string | null;
  price: number | null;
  currency: string | null;
  sku: string | null;
  expires: string | null;
};

export type ProcessedCourse = BaseCourse & {
  additionalMetadata: AdditionalMetadata[] | null;
  cardType?: string;
  weeksToComplete?: number;
  courseCount?: number;
  activeCourseRun: CourseRun | null;
  activeCourseRuns?: CourseRun[];
  isActive: boolean;
  inEdx: boolean;
  isPreview: boolean;
  hasOfacRestrictions: boolean;
  edxPath: string;
  subjects: ProcessedCourseSubject[] | null;
  entitlements?: CourseEntitlement[];
  organizationShortCodeOverride?: string;
  organizationLogoOverrideUrl?: string;
  _highlightResult?: unknown;
  onlyArchivedCourseRuns?: boolean;
};

export type CourseDetails = {
  productUuid: string;
  advertisedCourseRunUuid: string;
  productName: string;
  productShortDescription: string;
  productUrlSlug: string;
  levelType?: string | null;
  flexibility?: string | null;
  instructors: Omit<Instructor, "aboutMe">[];
  isActive: boolean;
  courseRunAvailability?: string[];
};

export type AdditionalMetadata = {
  startDate?: Date | null;
  created?: Date | null;
  endDate?: Date | null;
  externalIdentifier: string | null;
  externalUrl: string | null;
  leadCaptureFormUrl: string | null;
  modified?: Date | null;
  organicUrl: string | null;
  productStatus: string | null;
  courseTermOverride: string | null;
  externalCourseMarketingType: string | null;
  registrationDeadline: string | null;
  displayOnOrgPage: boolean | null;
  certificateInfo: {
    heading?: string | null;
    blurb?: string | null;
  }[];
  facts: {
    heading?: string | null;
    blurb?: string | null;
  }[];
  productMeta: {
    title?: string | null;
    description?: string | null;
    keywords?:
      | {
          name?: string | null;
          slug?: string | null;
        }[]
      | null;
  }[];
  taxiForm: {
    formId?: string | null;
    grouping?: string | null;
    title?: string | null;
    subtitle?: string | null;
    postSubmitUrl?: string | null;
  }[];
};

export type SitemapCourse = {
  productName: string;
  productUrlSlug: string;
  lastUpdated: Date;
};

export type { CourseFilter } from "./CourseFilter";
